import { type ReactNode, useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import RenderIf from "common/components/RenderIf";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import {
  ContentContainer,
  ContentHeader,
  ContentScroll,
  PageTitle,
} from "common/layouts/WorkspaceLayout";
import type { WorkspaceAccount } from "common/types";

import useAccountsFilters from "../hooks/useAccountsFilters";
import AccountSettings from "./AccountSettings";
import AddAccount from "./AddAccount";
import DesktopFilters from "./DesktopLayout/DesktopFilters";
import DesktopFilterTags from "./DesktopLayout/DesktopFilterTags";
import DesktopTable from "./DesktopLayout/DesktopTable";
import ManageAccounts from "./ManageAccounts";
import MobileFilters from "./MobileLayout/MobileFilters";
import MobileList from "./MobileLayout/MobileList";
import PendingRentedAccounts from "./PendingRentedAccounts";
import RentAccountsButton from "./RentAccountsButton";

import AccountsPlaceholder from "assets/images/empty-placeholders/accounts.png";
import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

export default function LinkedInAccounts() {
  const isDesktop = useTwBreakpoint("lg");
  const [selectedAccounts, setSelectedAccounts] = useState<WorkspaceAccount[]>(
    [],
  );

  const { accounts, accounts_count: accountsCount } =
    useSelectedWorkspaceContext();

  const hasAccounts = accountsCount > 0;

  const {
    filteredAccounts,
    hasSearchParams,
    searchParams,
    setSearchParams,
    searchText,
    setSearchText,
    clearSearchParams,
  } = useAccountsFilters(accounts);

  useEffect(() => {
    setSelectedAccounts((prev) =>
      prev.filter(({ id }) =>
        filteredAccounts.some((filteredAccount) => filteredAccount.id === id),
      ),
    );
  }, [filteredAccounts]);

  let mainContent: ReactNode;

  if (!hasAccounts) {
    mainContent = (
      <EmptyPlaceholder
        imageSrc={AccountsPlaceholder}
        title="No accounts added yet"
        subtitle="Connect your LinkedIn account to create campaigns, manage, and chat with your leads"
        actionComponent={
          <div className="grid gap-2.5 [&:has(>:only-child)]:grid-cols-1 [&:has(>:nth-child(2))]:grid-cols-2">
            <RenderIf condition={isDesktop}>
              <RentAccountsButton />
            </RenderIf>
            <AddAccount />
          </div>
        }
      />
    );
  } else {
    const clearSearchParamsButton = (
      <RenderIf condition={hasSearchParams}>
        <Button
          variant="link-danger"
          size={isDesktop ? "sm" : "md"}
          onClick={clearSearchParams}
          className="lg:mt-1"
        >
          Clear all
        </Button>
      </RenderIf>
    );

    const searchInputComponent = (
      <SearchInput
        variant={isDesktop ? "md" : "lg"}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onClear={() => setSearchText("")}
        placeholder="Search accounts"
        className="lg:w-80"
      />
    );
    const AccountFilters = isDesktop ? DesktopFilters : MobileFilters;
    const accountFiltersComponent = (
      <AccountFilters
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        clearSearchParamsButton={clearSearchParamsButton}
      />
    );

    const Component = isDesktop ? DesktopTable : MobileList;

    mainContent = (
      <>
        <div className="flex gap-x-2.5">
          {searchInputComponent}
          {accountFiltersComponent}
        </div>

        {hasSearchParams && (
          <div className="flex items-start justify-between max-lg:hidden">
            <DesktopFilterTags
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
            {clearSearchParamsButton}
          </div>
        )}

        {filteredAccounts.length ? (
          <Component
            accounts={filteredAccounts}
            selectedAccounts={selectedAccounts}
            setSelectedAccounts={setSelectedAccounts}
          />
        ) : (
          <EmptyPlaceholder
            imageSrc={NoResultsImage}
            title="No results found"
            subtitle="There were no results found for your search. Try searching for something else"
            actionComponent={
              <Button
                onClick={() => {
                  setSearchText("");
                  clearSearchParams();
                }}
              >
                Clear Search
              </Button>
            }
          />
        )}
      </>
    );
  }

  const primaryActionComponent = selectedAccounts.length ? (
    <ManageAccounts selectedAccounts={selectedAccounts} />
  ) : (
    <AddAccount />
  );

  return (
    <div className="flex flex-1 flex-col lg:flex-row gap-x-2 h-full">
      <ContentContainer className="relative">
        <ContentHeader>
          <PageTitle />

          <RenderIf condition={hasAccounts}>
            {isDesktop ? (
              <div className="flex items-center gap-x-2.5">
                <RentAccountsButton />
                {primaryActionComponent}
              </div>
            ) : (
              primaryActionComponent
            )}
          </RenderIf>
        </ContentHeader>
        <ContentScroll className="p-4 gap-y-4">
          <PendingRentedAccounts />
          {mainContent}
        </ContentScroll>
      </ContentContainer>
      <Routes>
        <Route path="/:accountId" element={<AccountSettings />} />
      </Routes>
    </div>
  );
}
